import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/ShipmentHistory';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const SHIPMENT_HISTORY_API_REDUCER_KEY = 'shipment-histories';
export const SHIPMENT_HISTORY_PATH = 'shipment-histories';

export const shipmentHistoryApi = createApi({
    reducerPath: SHIPMENT_HISTORY_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['ShipmentHistory', 'ShipmentHistories'],
    endpoints: (builder) => ({
        getShipmentHistoryList: builder.query<PaginationResult<Entity.ShipmentHistory>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ SHIPMENT_HISTORY_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['ShipmentHistories'],
        }),
        getShipmentHistory: builder.query<Entity.ShipmentHistory, Entity.PrimaryShipmentHistory>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = shipmentHistoryNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ SHIPMENT_HISTORY_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['ShipmentHistory'],
        }),
        getShipmentHistoryMultiple: builder.query<Entity.ShipmentHistory, Entity.PrimaryShipmentHistory[]>({
            query: (ids) => {
                return ({
                    url: `/${ SHIPMENT_HISTORY_PATH }/ids${Entity.PrimaryShipmentHistory.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['ShipmentHistories'],
        }),
        getShipmentHistoryPresentation: builder.query<PaginationResult<Entity.ShipmentHistory>, Entity.PresentationShipmentHistoryRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ SHIPMENT_HISTORY_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['ShipmentHistories'],
        }),
        
        insertShipmentHistory: builder.mutation<Entity.ShipmentHistory, Entity.CreateShipmentHistory>({
            query: (toInsert) => {
                return ({
                    url: `/${ SHIPMENT_HISTORY_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    shipmentHistoryApi.util.upsertQueryData('getShipmentHistory', Entity.PrimaryShipmentHistory.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['ShipmentHistories'],
        }),
        insertMultipleShipmentHistory: builder.mutation<Entity.ShipmentHistory[], Entity.CreateShipmentHistory[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ SHIPMENT_HISTORY_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            shipmentHistoryApi.util.upsertQueryData('getShipmentHistory', Entity.PrimaryShipmentHistory.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['ShipmentHistories'],
        }),
        
        deleteShipmentHistory: builder.mutation<Entity.ShipmentHistory, Entity.ShipmentHistory>({
            query: (toDelete) => {
                return ({
                    url: `/${ SHIPMENT_HISTORY_PATH }/${Entity.PrimaryShipmentHistory.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ShipmentHistories'],
        }),
        deleteMultipleShipmentHistory: builder.mutation<Entity.ShipmentHistory[], Entity.PrimaryShipmentHistory[]>({
            query: (ids) => {
                return ({
                    url: `/${ SHIPMENT_HISTORY_PATH }/batch${Entity.PrimaryShipmentHistory.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ShipmentHistories'],
        }),
    }),
})

export const [shipmentHistoryNormalizer, shipmentHistoryNormalizerMiddleware] = createNormalizationMiddleware(SHIPMENT_HISTORY_API_REDUCER_KEY, shipmentHistoryApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetShipmentHistoryListQuery,
    useGetShipmentHistoryMultipleQuery,
    useGetShipmentHistoryPresentationQuery,
    useGetShipmentHistoryQuery,
    useInsertShipmentHistoryMutation,
    useInsertMultipleShipmentHistoryMutation,
    
    useDeleteShipmentHistoryMutation,
    useDeleteMultipleShipmentHistoryMutation,
    
} = shipmentHistoryApi
